import type { LscArchiveItem } from '../../types';
import { displaySeasonBesideActiveEdition, displaySeasonLabel } from '../../seasonDisplay';
import {
  filterArchivesByPlayback,
  type ArchivePlaybackFilterMode,
} from '../../archivePlayback';
import {
  getArchiveDisplayTitle,
} from '../leagueHome/leagueHomeUtils';

export type { ArchivePlaybackFilterMode };

export const UNKNOWN_SEASON_LABEL = 'Season Unknown';
export const UNKNOWN_STAGE_LABEL = 'Unsorted Replays';

export type ArchiveLibraryFilters = {
  search: string;
  season: string;
  stage: string;
  team: string;
  broadcaster: string;
  playback: ArchivePlaybackFilterMode;
};

export type ArchiveLibraryStageGroup = {
  stageLabel: string;
  archives: LscArchiveItem[];
};

export type ArchiveLibrarySeasonGroup = {
  seasonLabel: string;
  stages: ArchiveLibraryStageGroup[];
};

export type ArchiveLibraryFilterOptions = {
  seasons: string[];
  stages: string[];
  teams: string[];
  broadcasters: string[];
};

export function extractArchiveSeasonLabel(
  archive: LscArchiveItem,
  activeEdition?: string | null
): string {
  const stored = archive.match_summary?.season_label?.trim() || '';
  if (!stored) return UNKNOWN_SEASON_LABEL;
  return (
    displaySeasonBesideActiveEdition(stored, archive.match_summary?.edition, activeEdition) ||
    UNKNOWN_SEASON_LABEL
  );
}

export function extractArchiveStageLabel(archive: LscArchiveItem): string {
  return archive.match_summary?.stage_label?.trim() || UNKNOWN_STAGE_LABEL;
}

function parseLabelNumber(label: string): number {
  const match = label.match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : -1;
}

function compareLabelsDesc(a: string, b: string): number {
  const unknownLabels = new Set([UNKNOWN_SEASON_LABEL, UNKNOWN_STAGE_LABEL]);
  const aUnknown = unknownLabels.has(a);
  const bUnknown = unknownLabels.has(b);
  if (aUnknown && !bUnknown) return 1;
  if (!aUnknown && bUnknown) return -1;

  const numA = parseLabelNumber(a);
  const numB = parseLabelNumber(b);
  if (numA >= 0 && numB >= 0 && numA !== numB) {
    return numB - numA;
  }
  return b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' });
}

function comparePublishedDesc(a: LscArchiveItem, b: LscArchiveItem): number {
  const timeA = a.published_at ? Date.parse(a.published_at) : 0;
  const timeB = b.published_at ? Date.parse(b.published_at) : 0;
  return timeB - timeA;
}

export function archiveMatchesSearch(archive: LscArchiveItem, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  const haystack = [
    getArchiveDisplayTitle(archive),
    archive.match_summary?.home_side_label,
    archive.match_summary?.away_side_label,
    archive.provider_broadcaster_login,
    archive.match_summary?.season_label,
    displaySeasonLabel(
      archive.match_summary?.season_label,
      null,
      archive.match_summary?.edition
    ),
    archive.match_summary?.stage_label,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalized);
}

export function archiveMatchesTeamFilter(archive: LscArchiveItem, team: string): boolean {
  if (!team) return true;
  const home = archive.match_summary?.home_side_label?.trim() ?? '';
  const away = archive.match_summary?.away_side_label?.trim() ?? '';
  return home === team || away === team;
}

export function filterArchiveLibraryItems(
  archives: LscArchiveItem[],
  filters: ArchiveLibraryFilters,
  activeEdition?: string | null
): LscArchiveItem[] {
  const playbackFiltered = filterArchivesByPlayback(archives, filters.playback);
  return playbackFiltered.filter((archive) => {
    if (!archiveMatchesSearch(archive, filters.search)) return false;
    if (filters.season && extractArchiveSeasonLabel(archive, activeEdition) !== filters.season) {
      return false;
    }
    if (filters.stage && extractArchiveStageLabel(archive) !== filters.stage) return false;
    if (!archiveMatchesTeamFilter(archive, filters.team)) return false;
    if (
      filters.broadcaster &&
      (archive.provider_broadcaster_login?.trim() ?? '') !== filters.broadcaster
    ) {
      return false;
    }
    return true;
  });
}

export function buildArchiveLibraryFilterOptions(
  archives: LscArchiveItem[],
  activeEdition?: string | null
): ArchiveLibraryFilterOptions {
  const seasons = new Set<string>();
  const stages = new Set<string>();
  const teams = new Set<string>();
  const broadcasters = new Set<string>();

  for (const archive of archives) {
    seasons.add(extractArchiveSeasonLabel(archive, activeEdition));
    stages.add(extractArchiveStageLabel(archive));
    const home = archive.match_summary?.home_side_label?.trim();
    const away = archive.match_summary?.away_side_label?.trim();
    if (home) teams.add(home);
    if (away) teams.add(away);
    const broadcaster = archive.provider_broadcaster_login?.trim();
    if (broadcaster) broadcasters.add(broadcaster);
  }

  const sortAlpha = (values: Set<string>) =>
    Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

  return {
    seasons: sortAlpha(seasons),
    stages: sortAlpha(stages),
    teams: sortAlpha(teams),
    broadcasters: sortAlpha(broadcasters),
  };
}

export function groupArchivesBySeasonAndStage(
  archives: LscArchiveItem[],
  activeEdition?: string | null
): ArchiveLibrarySeasonGroup[] {
  const seasonMap = new Map<string, Map<string, LscArchiveItem[]>>();

  for (const archive of archives) {
    const seasonLabel = extractArchiveSeasonLabel(archive, activeEdition);
    const stageLabel = extractArchiveStageLabel(archive);
    if (!seasonMap.has(seasonLabel)) {
      seasonMap.set(seasonLabel, new Map());
    }
    const stageMap = seasonMap.get(seasonLabel)!;
    if (!stageMap.has(stageLabel)) {
      stageMap.set(stageLabel, []);
    }
    stageMap.get(stageLabel)!.push(archive);
  }

  return Array.from(seasonMap.entries())
    .sort(([a], [b]) => compareLabelsDesc(a, b))
    .map(([seasonLabel, stageMap]) => ({
      seasonLabel,
      stages: Array.from(stageMap.entries())
        .sort(([a], [b]) => compareLabelsDesc(a, b))
        .map(([stageLabel, stageArchives]) => ({
          stageLabel,
          archives: [...stageArchives].sort(comparePublishedDesc),
        })),
    }));
}

export function hasActiveArchiveLibraryFilters(filters: ArchiveLibraryFilters): boolean {
  return Boolean(
    filters.search.trim() ||
      filters.season ||
      filters.stage ||
      filters.team ||
      filters.broadcaster ||
      filters.playback !== 'playable'
  );
}

export const EMPTY_ARCHIVE_LIBRARY_FILTERS: ArchiveLibraryFilters = {
  search: '',
  season: '',
  stage: '',
  team: '',
  broadcaster: '',
  playback: 'playable',
};
